export type RefKind =
  | 'patient'
  | 'session'
  | 'document'
  | 'template'
  | 'routine'
  | 'project'
  | 'task'
  | 'letter'
  | 'file'
  | 'collection';

export type SessionScopedRefKind = 'document' | 'letter';

export type RefTarget =
  | { kind: SessionScopedRefKind; id: string; sessionId: string }
  | { kind: Exclude<RefKind, SessionScopedRefKind>; id: string };

// Not `Extract<RefTarget, { kind: K }>`, which is `never` for a single kind of a union arm.
export type RefTargetOf<K extends RefKind> = K extends SessionScopedRefKind
  ? { kind: K; id: string; sessionId: string }
  : { kind: K; id: string };

export type RefTag = RefTarget & { labelHint: string };

export type RefNormalization = {
  outcome: 'repaired' | 'unwrapped' | 'dropped';
  reason: string;
  kind: RefKind | null;
};

export const MAX_REF_TAG_BYTES = 512;
export const MAX_REF_LABEL_CHARS = 120;

const OBJECT_ID_PATTERN = /^[0-9A-Fa-f]{24}$/;
const SESSION_ID_PATTERN = /^[0-9]{1,39}$/;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const REF_KINDS: {
  readonly [K in RefKind]: {
    idPattern: RegExp;
    sessionScoped: K extends SessionScopedRefKind ? true : false;
  };
} = {
  patient: { idPattern: OBJECT_ID_PATTERN, sessionScoped: false },
  session: { idPattern: SESSION_ID_PATTERN, sessionScoped: false },
  document: { idPattern: OBJECT_ID_PATTERN, sessionScoped: true },
  template: { idPattern: OBJECT_ID_PATTERN, sessionScoped: false },
  routine: { idPattern: /^[A-Za-z0-9_-]{1,64}$/, sessionScoped: false },
  project: { idPattern: OBJECT_ID_PATTERN, sessionScoped: false },
  task: { idPattern: OBJECT_ID_PATTERN, sessionScoped: false },
  letter: { idPattern: OBJECT_ID_PATTERN, sessionScoped: true },
  file: { idPattern: UUID_PATTERN, sessionScoped: false },
  collection: { idPattern: UUID_PATTERN, sessionScoped: false },
};

export class RefTagError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'RefTagError';
    this.code = code;
  }
}

// Python's `str.isspace` / `re` `\s` set, which is not JS `\s`: it adds \x1c-\x1f and \x85, and omits \ufeff.
const INLINE_SPACE_CHARS = String.raw`\t\v\f\r\x1c-\x20\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000`;
const SPACE_CHARS = String.raw`\n${INLINE_SPACE_CHARS}`;
const INLINE_SPACE = `[${INLINE_SPACE_CHARS}]*`;
const WORD_OR_HYPHEN = String.raw`[\p{L}\p{N}_-]`;

const CANONICAL_TAG_SOURCE =
  '<ref data-kind="(?<kind>[a-z]+)" data-id="(?<id>[^"]*)"' +
  '(?: data-session-id="(?<session_id>[^"]*)")?>(?<body>[^<]*)</ref>';
const CANONICAL_TAG_PATTERN = new RegExp(CANONICAL_TAG_SOURCE, 'g');
const CANONICAL_TAG_EXACT = new RegExp(`^(?:${CANONICAL_TAG_SOURCE})$`);

const ATTRIBUTE_NAME = String.raw`[^${SPACE_CHARS}"'=<>/\\]+`;
const ATTRIBUTE_VALUE = String.raw`\\"[^"\\<>\n]*(?:\\")?|"[^"<>\n]*"?|'[^'<>\n]*'?|[^${SPACE_CHARS}"'<>/\\]+`;
const ATTRIBUTE_PATTERN = new RegExp(
  `(${ATTRIBUTE_NAME})${INLINE_SPACE}=${INLINE_SPACE}(${ATTRIBUTE_VALUE})`,
  'gu'
);
const MARKUP_PATTERN = new RegExp(
  `<ref(?!${WORD_OR_HYPHEN})(?:(?<opening>[^<>\\n]{0,${MAX_REF_TAG_BYTES}})>` +
    `|(?:${INLINE_SPACE}${ATTRIBUTE_NAME}${INLINE_SPACE}=${INLINE_SPACE}` +
    `(?:${ATTRIBUTE_VALUE}))*)` +
    `|(?<close></ref(?!${WORD_OR_HYPHEN})(?:[${SPACE_CHARS}]*>)?)`,
  'giu'
);

const OPENING_PATTERN = new RegExp(`<ref(?!${WORD_OR_HYPHEN})`, 'giu');
const COMPLETE_CLOSE_PATTERN = new RegExp(`</ref[${SPACE_CHARS}]*>`, 'giu');
const PARTIAL_MARKUP_TAIL = new RegExp(
  `<(?:re?|/(?:r(?:e(?:f[${SPACE_CHARS}]*)?)?)?)?$`,
  'iu'
);

const LABEL_SPACE_PATTERN = new RegExp(`[${SPACE_CHARS}\\p{Cc}]`, 'gu');
const TRAILING_SPACE_PATTERN = new RegExp(`[${SPACE_CHARS}]+$`, 'u');
const LONE_SURROGATE_PATTERN = /\p{Cs}/u;
const CHARREF_PATTERN = /&(#[0-9]+;?|#[xX][0-9a-fA-F]+;?|[^\t\n\f <&#;]{1,32};?)/gu;

const NAMED_CHARREFS = new Map<string, string>([
  ['amp;', '&'],
  ['amp', '&'],
  ['AMP;', '&'],
  ['AMP', '&'],
  ['lt;', '<'],
  ['lt', '<'],
  ['LT;', '<'],
  ['LT', '<'],
  ['gt;', '>'],
  ['gt', '>'],
  ['GT;', '>'],
  ['GT', '>'],
  ['quot;', '"'],
  ['quot', '"'],
  ['QUOT;', '"'],
  ['QUOT', '"'],
  ['apos;', "'"],
  ['nbsp;', '\u00a0'],
  ['nbsp', '\u00a0'],
]);
const WINDOWS_1252_C1 =
  '\u20ac\x81\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\x8d\u017d\x8f' +
  '\x90\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\x9d\u017e\u0178';

const isRefKind = (kind: string): kind is RefKind => Object.hasOwn(REF_KINDS, kind);

const searchFrom = (
  pattern: RegExp,
  text: string,
  position: number
): RegExpExecArray | null => {
  pattern.lastIndex = position;
  return pattern.exec(text);
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

const isDroppedCodePoint = (codePoint: number): boolean =>
  (codePoint >= 0x1 && codePoint <= 0x8) ||
  codePoint === 0xb ||
  (codePoint >= 0xe && codePoint <= 0x1f) ||
  codePoint === 0x7f ||
  (codePoint >= 0xfdd0 && codePoint <= 0xfdef) ||
  (codePoint & 0xfffe) === 0xfffe;

const decodeNumericCharref = (codePoint: number): string => {
  if (codePoint === 0) return '\ufffd';
  if (codePoint === 0xd) return '\r';
  if (codePoint >= 0x80 && codePoint <= 0x9f) {
    return WINDOWS_1252_C1.charAt(codePoint - 0x80);
  }
  if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || codePoint > 0x10ffff) {
    return '\ufffd';
  }
  if (isDroppedCodePoint(codePoint)) return '';
  return String.fromCodePoint(codePoint);
};

const decodeCharref = (reference: string): string => {
  if (reference.startsWith('#')) {
    const isHex = reference[1] === 'x' || reference[1] === 'X';
    const digits = reference.slice(isHex ? 2 : 1).replace(';', '');
    return decodeNumericCharref(parseInt(digits, isHex ? 16 : 10));
  }
  const named = NAMED_CHARREFS.get(reference);
  if (named !== undefined) return named;
  for (let end = reference.length - 1; end > 1; end -= 1) {
    const prefix = NAMED_CHARREFS.get(reference.slice(0, end));
    if (prefix !== undefined) return prefix + reference.slice(end);
  }
  return `&${reference}`;
};

const unescapeHtml = (value: string): string =>
  value.includes('&')
    ? value.replace(CHARREF_PATTERN, (_match, reference: string) =>
        decodeCharref(reference)
      )
    : value;

const utf8Length = (value: string): number => {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint < 0x80) bytes += 1;
    else if (codePoint < 0x800) bytes += 2;
    else if (codePoint < 0x10000) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

const normalizeLabel = (label: string): string => {
  const normalized = label.replace(LABEL_SPACE_PATTERN, ' ').replace(/^ +| +$/g, '');
  if (!normalized) throw new RefTagError('empty_label');
  if (LONE_SURROGATE_PATTERN.test(normalized)) throw new RefTagError('invalid_utf8');
  return normalized;
};

const toRefTarget = (
  kind: string,
  id: string,
  sessionId: string | undefined
): RefTarget => {
  if (!isRefKind(kind)) throw new RefTagError('unknown_kind');
  const spec = REF_KINDS[kind];
  if (!spec.idPattern.test(id)) throw new RefTagError('bad_id_shape');
  if (!spec.sessionScoped) {
    if (sessionId !== undefined) throw new RefTagError('unexpected_session_id');
    return { kind, id } as RefTarget;
  }
  if (sessionId === undefined) throw new RefTagError('missing_session_id');
  if (!SESSION_ID_PATTERN.test(sessionId)) throw new RefTagError('bad_session_id');
  return { kind, id, sessionId };
};

const formatParts = (target: RefTarget, label: string): string => {
  const session = 'sessionId' in target ? ` data-session-id="${target.sessionId}"` : '';
  return `<ref data-kind="${target.kind}" data-id="${target.id}"${session}>${escapeHtml(label)}</ref>`;
};

type CanonicalMatch = {
  start: number;
  end: number;
  kind: string;
  id: string;
  sessionId: string | undefined;
  body: string;
};

const canonicalMatches = (text: string): CanonicalMatch[] =>
  Array.from(text.matchAll(CANONICAL_TAG_PATTERN), (match) => {
    const { kind = '', id = '', session_id: sessionId, body = '' } = match.groups ?? {};
    const start = match.index ?? 0;
    return { start, end: start + match[0].length, kind, id, sessionId, body };
  });

const toRefTag = (match: CanonicalMatch): RefTag => ({
  ...toRefTarget(match.kind, match.id, match.sessionId),
  labelHint: unescapeHtml(match.body),
});

const orNullOnRefTagError = <T>(read: () => T): T | null => {
  try {
    return read();
  } catch (error) {
    if (error instanceof RefTagError) return null;
    throw error;
  }
};

export const refTargetFrom = (
  kind: string,
  id: string,
  sessionId?: string
): RefTarget | null => orNullOnRefTagError(() => toRefTarget(kind, id, sessionId));

// rehype-sanitize prefixes clobberable attribute values with this (DOM clobber defense).
const CLOBBER_PREFIX = 'user-content-';

/** The first non-blank attribute value, without the sanitizer's clobber prefix. */
export const unclobberedAttr = (...values: Array<string | undefined>): string => {
  for (const value of values) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed) {
      return trimmed.startsWith(CLOBBER_PREFIX)
        ? trimmed.slice(CLOBBER_PREFIX.length)
        : trimmed;
    }
  }
  return '';
};

export const formatRefTag = (tag: RefTag): string => {
  const sessionId = (tag as { sessionId?: string }).sessionId;
  const target = toRefTarget(tag.kind, tag.id, sessionId);
  // Python slices code points, not UTF-16 units; the byte cap is then applied per code point.
  const characters = Array.from(normalizeLabel(tag.labelHint)).slice(
    0,
    MAX_REF_LABEL_CHARS
  );
  while (characters.length > 0) {
    const formatted = formatParts(target, characters.join(''));
    if (utf8Length(formatted) <= MAX_REF_TAG_BYTES) return formatted;
    characters.pop();
  }
  throw new RefTagError('tag_too_long');
};

export const parseRefTags = (text: string): RefTag[] =>
  canonicalMatches(text).map(toRefTag);

export const stripRefTags = (
  text: string,
  label?: (target: RefTarget) => string | undefined
): string => {
  let stripped = '';
  let position = 0;
  for (const match of canonicalMatches(text)) {
    const target = label ? refTargetFrom(match.kind, match.id, match.sessionId) : null;
    const resolved = target ? label?.(target) : undefined;
    stripped +=
      text.slice(position, match.start) + (resolved ?? unescapeHtml(match.body));
    position = match.end;
  }
  return stripped + text.slice(position);
};

const repairRefTag = (
  kind: string,
  attributes: Map<string, string>,
  body: string,
  label: string
): string | RefTagError => {
  try {
    if (body.includes('\n')) throw new RefTagError('multiline_label');
    const tag: RefTag = {
      ...toRefTarget(
        kind,
        attributes.get('data-id') ?? '',
        attributes.get('data-session-id')
      ),
      labelHint: label,
    };
    const formatted = formatRefTag(tag);
    if (formatted !== formatParts(tag, normalizeLabel(label))) {
      throw new RefTagError('label_too_long');
    }
    return formatted;
  } catch (error) {
    if (error instanceof RefTagError) return error;
    throw error;
  }
};

export const normalizeRefTags = (
  text: string
): { text: string; events: RefNormalization[] } => {
  const pieces: string[] = [];
  const events: RefNormalization[] = [];
  let position = 0;
  for (
    let markup = searchFrom(MARKUP_PATTERN, text, position);
    markup !== null;
    markup = searchFrom(MARKUP_PATTERN, text, position)
  ) {
    pieces.push(text.slice(position, markup.index));
    position = markup.index + markup[0].length;
    if (markup.groups?.close !== undefined) {
      events.push({ outcome: 'dropped', reason: 'stray_close', kind: null });
      continue;
    }
    const attributes = new Map<string, string>();
    for (const [, name = '', value = ''] of markup[0].matchAll(ATTRIBUTE_PATTERN)) {
      attributes.set(name.toLowerCase(), value.replace(/^[\\"']+|[\\"']+$/g, ''));
    }
    const kind = (attributes.get('data-kind') ?? '').toLowerCase();
    const knownKind = isRefKind(kind) ? kind : null;
    const opening = markup.groups?.opening;
    const close =
      opening !== undefined && !opening.replace(TRAILING_SPACE_PATTERN, '').endsWith('/')
        ? searchFrom(MARKUP_PATTERN, text, position)
        : null;
    if (close === null || close.groups?.close === undefined) {
      events.push({ outcome: 'dropped', reason: 'unterminated', kind: knownKind });
      continue;
    }
    const closeEnd = close.index + close[0].length;
    const segment = text.slice(markup.index, closeEnd);
    const body = text.slice(position, close.index);
    const label = unescapeHtml(body);
    position = closeEnd;
    const repaired = repairRefTag(kind, attributes, body, label);
    if (repaired instanceof RefTagError) {
      events.push({ outcome: 'unwrapped', reason: repaired.code, kind: knownKind });
      pieces.push(searchFrom(MARKUP_PATTERN, label, 0) === null ? label : body);
      continue;
    }
    if (CANONICAL_TAG_EXACT.test(segment)) {
      pieces.push(segment);
    } else {
      events.push({ outcome: 'repaired', reason: 'attribute_drift', kind: knownKind });
      pieces.push(repaired);
    }
  }
  if (events.length === 0) return { text, events };
  pieces.push(text.slice(position));
  return { text: pieces.join(''), events };
};

export const withholdUnfinishedRef = (
  text: string
): { text: string; withheld: string } => {
  let lastOpening = -1;
  for (const match of text.matchAll(OPENING_PATTERN)) lastOpening = match.index ?? -1;
  const pendingOpening =
    lastOpening >= 0 && searchFrom(COMPLETE_CLOSE_PATTERN, text, lastOpening) === null;
  const cut = pendingOpening
    ? lastOpening
    : (PARTIAL_MARKUP_TAIL.exec(text)?.index ?? text.length);
  return { text: text.slice(0, cut), withheld: text.slice(cut) };
};

/** Withheld first: normalizing an unfinished `<ref` keeps its body, flashing the hint as prose. */
export const prepareRefTagsForRender = (
  text: string,
  { streaming = false }: { streaming?: boolean } = {}
): string => normalizeRefTags(streaming ? withholdUnfinishedRef(text).text : text).text;

export const splitRefSegments = (
  text: string
): Array<{ type: 'text'; text: string } | { type: 'ref'; ref: RefTag }> => {
  const segments: Array<{ type: 'text'; text: string } | { type: 'ref'; ref: RefTag }> =
    [];
  let position = 0;
  for (const match of canonicalMatches(text)) {
    const ref = orNullOnRefTagError(() => toRefTag(match));
    if (ref === null) continue;
    if (match.start > position) {
      segments.push({ type: 'text', text: text.slice(position, match.start) });
    }
    segments.push({ type: 'ref', ref });
    position = match.end;
  }
  if (position < text.length) segments.push({ type: 'text', text: text.slice(position) });
  return segments;
};
